import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Card, CardContent } from '../components/ui/Card';
import Input from '../components/ui/Input';
import Button from '../components/ui/Button';
import { FunctionalSelect, SelectItem } from '../components/ui/Select';
import ErrorDialog from '../components/ui/ErrorDialog';
import ConfirmDialog from '../components/ui/ConfirmDialog';
import ListPagination, { LIST_PAGE_SIZE } from '../components/ui/ListPagination';
import {
  SEGMENTS_LIST_PAGE_KEY,
  SEGMENT_TEMPLATES_LIST_PAGE_KEY,
  listPageState,
  useListPage,
} from '../utils/listPage';
import { criteriaToText } from '../components/segmentBuilder/criteriaText';
import TemplatePicker from '../components/segmentBuilder/TemplatePicker';
import { canManageTemplates, segmentTemplatesService } from '../services/segmentTemplatesService';
import { useAuth } from '../contexts/AuthContext';
import { HEADER_SELECT_CLASS, PageHeaderActions } from '../components/PageHeaderActions';
import { toast } from 'react-hot-toast';
import { api } from '../services/authService';
import { organizationsService } from '../services/organizationsService';
import { wakeSqlWarehouse } from '../services/sqlWarehouseService';
import { buildFileDownloadHref } from '../services/fileDownload';
import { softWrap } from '../utils/softWrap';
import { ArrowDown, ArrowUp, ChevronRight, ChevronDown, Plus, Pencil, Trash2, Copy, Download, Play, Search, LayoutTemplate } from 'lucide-react';

const DEFAULT_SORT_BY_FIELD = 'updated_at';
const DEFAULT_SORT_ORDER = 'desc';
const DEFAULT_TEMPLATE_SORT_BY_FIELD = 'updated_at';
const DEFAULT_TEMPLATE_SORT_ORDER = 'desc';

function formatApiErrorDetail(detail) {
  if (detail == null) return '';
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail)) {
    return detail.map((e) => (e && e.msg != null ? e.msg : JSON.stringify(e))).join('\n');
  }
  if (typeof detail === 'object') return JSON.stringify(detail);
  return String(detail);
}

function formatDate(value) {
  if (!value) return '—';
  return new Date(value).toLocaleDateString();
}

function compareSegmentRows(a, b, field, order) {
  const av = a?.[field];
  const bv = b?.[field];
  const aMissing = av == null || av === '';
  const bMissing = bv == null || bv === '';
  if (aMissing && bMissing) return 0;
  if (aMissing) return 1;
  if (bMissing) return -1;

  let cmp;
  if (field === 'created_at' || field === 'updated_at') {
    const at = Date.parse(av);
    const bt = Date.parse(bv);
    cmp = Number.isNaN(at) || Number.isNaN(bt) ? String(av).localeCompare(String(bv)) : at - bt;
  } else if (field === 'id') {
    cmp = Number(av) - Number(bv);
  } else {
    cmp = String(av).localeCompare(String(bv), undefined, { sensitivity: 'base' });
  }
  return order === 'asc' ? cmp : -cmp;
}

function formatMd5Count(value) {
  if (value == null) return '—';
  return Number(value).toLocaleString();
}

function compareTemplateRows(a, b, field, order) {
  const av = a?.[field];
  const bv = b?.[field];
  const aMissing = av == null || av === '';
  const bMissing = bv == null || bv === '';
  if (aMissing && bMissing) return 0;
  if (aMissing) return 1;
  if (bMissing) return -1;

  let cmp;
  if (field === 'updated_at' || field === 'created_at') {
    const at = Date.parse(av);
    const bt = Date.parse(bv);
    cmp = Number.isNaN(at) || Number.isNaN(bt) ? String(av).localeCompare(String(bv)) : at - bt;
  } else if (field === 'md5_count') {
    cmp = Number(av) - Number(bv);
  } else {
    cmp = String(av).localeCompare(String(bv), undefined, { sensitivity: 'base' });
  }
  return order === 'asc' ? cmp : -cmp;
}

function titleCase(value) {
  if (!value) return '';
  const s = String(value);
  return s.charAt(0).toUpperCase() + s.slice(1).toLowerCase();
}

function formatSegmentSchedule(segment) {
  const schedule = segment?.schedule;
  if (!schedule) return '—';
  const base = titleCase(schedule);
  if (schedule === 'weekly' && segment.schedule_day_of_week) {
    return `${base} (${titleCase(segment.schedule_day_of_week)})`;
  }
  if (schedule === 'monthly' && segment.schedule_day_of_month) {
    const dom = segment.schedule_day_of_month;
    const domLabel = /^\d+$/.test(dom) ? dom : titleCase(dom);
    return `${base} (${domLabel})`;
  }
  return base;
}

function formatSegmentNameForDisplay(name) {
  return softWrap(name, 30);
}

function segmentDownloadStatusBadgeClass(processingStatus) {
  const s = (processingStatus || '').toLowerCase();
  if (s === 'complete' || s === 'completed') return 'bg-blue-100 text-blue-800';
  if (s === 'failed') return 'bg-red-100 text-red-800';
  if (s === 'pending') return 'bg-yellow-100 text-yellow-800';
  if (s === 'started' || s === 'processing') return 'bg-green-100 text-green-800';
  return 'bg-gray-100 text-gray-800';
}

function isTerminalSegmentDownloadStatus(processingStatus) {
  const s = (processingStatus || '').toLowerCase();
  return s === 'complete' || s === 'completed' || s === 'failed';
}

function canDownloadSegment(segment) {
  const s = (segment.processing_status || '').toLowerCase();
  return Boolean(segment.execution_id) && (s === 'complete' || s === 'completed');
}

const Segments = () => {
  const { listOffset, currentPage, goToPage, resetPage } = useListPage({
    storageKey: SEGMENTS_LIST_PAGE_KEY,
  });
  const [organizations, setOrganizations] = useState([]);
  const [selectedOrganization, setSelectedOrganization] = useState('');
  const [segments, setSegments] = useState([]);
  const [nameSearchInput, setNameSearchInput] = useState('');
  const [nameContains, setNameContains] = useState('');
  const [sortByField, setSortByField] = useState(DEFAULT_SORT_BY_FIELD);
  const [sortOrder, setSortOrder] = useState(DEFAULT_SORT_ORDER);
  const [loading, setLoading] = useState(false);
  const [expandedSegmentId, setExpandedSegmentId] = useState(null);
  const [showErrorDialog, setShowErrorDialog] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  const navigate = useNavigate();
  const { user } = useAuth();
  const [templatePickerOpen, setTemplatePickerOpen] = useState(false);

  const [segmentToDelete, setSegmentToDelete] = useState(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [runningSegmentId, setRunningSegmentId] = useState(null);

  const isFastCubeAdmin = canManageTemplates(user);
  const [searchParams, setSearchParams] = useSearchParams();
  const activeTab = searchParams.get('tab') === 'templates' ? 'templates' : 'segments';
  const setActiveTab = (tab) => {
    const next = new URLSearchParams(searchParams);
    if (tab === 'templates') next.set('tab', 'templates');
    else next.delete('tab');
    setSearchParams(next, { replace: true });
  };

  const {
    listOffset: templateListOffset,
    currentPage: templateCurrentPage,
    goToPage: goToTemplatePage,
    resetPage: resetTemplatePage,
  } = useListPage({ storageKey: SEGMENT_TEMPLATES_LIST_PAGE_KEY, paramName: 'tpage' });
  const [templates, setTemplates] = useState([]);
  const [templateNameSearchInput, setTemplateNameSearchInput] = useState('');
  const [templateNameContains, setTemplateNameContains] = useState('');
  const [templatesLoading, setTemplatesLoading] = useState(false);
  const [templateSortByField, setTemplateSortByField] = useState(DEFAULT_TEMPLATE_SORT_BY_FIELD);
  const [templateSortOrder, setTemplateSortOrder] = useState(DEFAULT_TEMPLATE_SORT_ORDER);
  const [templateToDelete, setTemplateToDelete] = useState(null);
  const [showDeleteTemplateConfirm, setShowDeleteTemplateConfirm] = useState(false);
  const [deletingTemplate, setDeletingTemplate] = useState(false);
  const [expandedTemplateId, setExpandedTemplateId] = useState(null);

  useEffect(() => {
    wakeSqlWarehouse();

    const loadOrganizations = async () => {
      try {
        const orgs = await organizationsService.getOrganizations();
        setOrganizations(orgs);

        const savedSelected = organizationsService.getSelectedOrganization('id');
        if (savedSelected && orgs.some((o) => String(o.id) === String(savedSelected))) {
          setSelectedOrganization(String(savedSelected));
          organizationsService.setSelectedOrganization(savedSelected, 'id', orgs);
        } else if (orgs.length > 0) {
          const defaultId = String(orgs[0].id);
          setSelectedOrganization(defaultId);
          organizationsService.setSelectedOrganization(defaultId, 'id', orgs);
        }
      } catch (error) {
        console.error('Error loading organizations:', error);
        setErrorMessage('Failed to load organizations. Please refresh the page and try again.');
        setShowErrorDialog(true);
      }
    };

    loadOrganizations();
  }, []);

  const loadSegments = useCallback(async ({ silent = false } = {}) => {
    if (!selectedOrganization) {
      setSegments([]);
      return;
    }

    if (!silent) setLoading(true);
    try {
      const params = {
        organization_id: selectedOrganization,
        sort_by_field: DEFAULT_SORT_BY_FIELD,
        sort_order: DEFAULT_SORT_ORDER,
      };
      if (nameContains) {
        params.name_contains = nameContains;
      }
      const res = await api.get('/api/v2/segments/', { params });
      const rows = Array.isArray(res.data) ? res.data : [];
      if (silent) {
        // Merge job-completion fields onto the list so status, count, and
        // last_run update together when a download finishes.
        setSegments((prev) => {
          const prevById = new Map(prev.map((s) => [s.id, s]));
          return rows.map((row) => {
            const previous = prevById.get(row.id);
            if (!previous) return row;
            return {
              ...previous,
              ...row,
              processing_status: row.processing_status,
              md5_count: row.md5_count,
              last_run: row.last_run,
              // Explicit null means criteria/SQL changed and the prior download is stale.
              execution_id:
                row.execution_id !== undefined ? row.execution_id : previous.execution_id,
              updated_at: row.updated_at,
            };
          });
        });
      } else {
        setSegments(rows);
        setSortByField(DEFAULT_SORT_BY_FIELD);
        setSortOrder(DEFAULT_SORT_ORDER);
      }
    } catch (e) {
      console.error('Failed to load segments:', e);
      if (!silent) {
        setSegments([]);
        setErrorMessage(
          formatApiErrorDetail(e.response?.data?.detail) ||
            e.message ||
            'Failed to load segments. Please try again.'
        );
        setShowErrorDialog(true);
      }
    } finally {
      if (!silent) setLoading(false);
    }
  }, [selectedOrganization, nameContains]);

  useEffect(() => {
    loadSegments();
  }, [loadSegments]);

  const sortedSegments = useMemo(() => {
    const copy = [...segments];
    copy.sort((a, b) => compareSegmentRows(a, b, sortByField, sortOrder));
    return copy;
  }, [segments, sortByField, sortOrder]);

  const pagedSegments = sortedSegments.slice(listOffset, listOffset + LIST_PAGE_SIZE);
  const totalPages = Math.max(1, Math.ceil(segments.length / LIST_PAGE_SIZE) || 1);

  // If filters/sort shrink the list past the current page, jump to the last valid page.
  useEffect(() => {
    if (loading || segments.length === 0) return;
    if (currentPage > totalPages) {
      goToPage(totalPages);
    }
  }, [loading, segments.length, currentPage, totalPages, goToPage]);

  const hasActiveSegmentDownloads = segments.some(
    (segment) => segment.processing_status && !isTerminalSegmentDownloadStatus(segment.processing_status)
  );

  useEffect(() => {
    if (!hasActiveSegmentDownloads) return undefined;
    loadSegments({ silent: true });
    const timer = setInterval(() => {
      loadSegments({ silent: true });
    }, 30000);
    return () => clearInterval(timer);
  }, [hasActiveSegmentDownloads, loadSegments]);

  const sortedOrganizations = [...organizations].sort((a, b) => a.name.localeCompare(b.name));

  const loadTemplates = useCallback(async () => {
    if (!selectedOrganization) {
      setTemplates([]);
      return;
    }
    setTemplatesLoading(true);
    try {
      const rows = await segmentTemplatesService.list(selectedOrganization);
      setTemplates(rows);
      setTemplateSortByField(DEFAULT_TEMPLATE_SORT_BY_FIELD);
      setTemplateSortOrder(DEFAULT_TEMPLATE_SORT_ORDER);
    } catch (e) {
      console.error('Failed to load segment templates:', e);
      setTemplates([]);
      setErrorMessage(
        formatApiErrorDetail(e.response?.data?.detail) || e.message || 'Failed to load templates. Please try again.'
      );
      setShowErrorDialog(true);
    } finally {
      setTemplatesLoading(false);
    }
  }, [selectedOrganization]);

  useEffect(() => {
    if (activeTab !== 'templates') return;
    loadTemplates();
  }, [activeTab, loadTemplates]);

  const filteredTemplates = useMemo(() => {
    const q = templateNameContains.trim().toLowerCase();
    if (!q) return templates;
    return templates.filter((t) => (t.name || '').toLowerCase().includes(q));
  }, [templates, templateNameContains]);

  const sortedTemplates = useMemo(() => {
    const copy = [...filteredTemplates];
    copy.sort((a, b) => compareTemplateRows(a, b, templateSortByField, templateSortOrder));
    return copy;
  }, [filteredTemplates, templateSortByField, templateSortOrder]);

  const pagedTemplates = sortedTemplates.slice(templateListOffset, templateListOffset + LIST_PAGE_SIZE);
  const templateTotalPages = Math.max(1, Math.ceil(filteredTemplates.length / LIST_PAGE_SIZE) || 1);

  // If sorting/search shrinks the list past the current page, jump to the last valid page.
  useEffect(() => {
    if (templatesLoading || filteredTemplates.length === 0) return;
    if (templateCurrentPage > templateTotalPages) {
      goToTemplatePage(templateTotalPages);
    }
  }, [templatesLoading, filteredTemplates.length, templateCurrentPage, templateTotalPages, goToTemplatePage]);

  const handleTemplateSortHeaderClick = (field) => {
    resetTemplatePage();
    if (templateSortByField === field) {
      setTemplateSortOrder((prev) => (prev === 'asc' ? 'desc' : 'asc'));
      return;
    }
    setTemplateSortByField(field);
    setTemplateSortOrder(field === 'name' ? 'asc' : 'desc');
  };

  const renderTemplateSortableHeader = (label, field, className) => {
    const isActive = templateSortByField === field;
    const SortIcon = templateSortOrder === 'asc' ? ArrowUp : ArrowDown;
    return (
      <th className={className}>
        <button
          type="button"
          onClick={() => handleTemplateSortHeaderClick(field)}
          className="inline-flex items-center gap-1 font-medium text-gray-700 hover:text-gray-900"
          aria-label={`Sort by ${label}${isActive ? `, currently ${templateSortOrder}` : ''}`}
        >
          {label}
          {isActive ? <SortIcon size={14} aria-hidden="true" /> : null}
        </button>
      </th>
    );
  };

  /** Legacy (org-independent) templates stay FastCube-admin-managed; org-owned ones follow org access. */
  const canManageTemplate = (t) => (t.organization_id == null ? isFastCubeAdmin : true);

  const handleDeleteTemplateClick = (template) => {
    setTemplateToDelete(template);
    setShowDeleteTemplateConfirm(true);
  };

  /** Open the editor pre-filled with this template as a new, unsaved template. */
  const handleCopyTemplate = (template) => {
    navigate(`/segments/templates/new?from=${template.id}`, {
      state: listPageState(templateCurrentPage, 'templateListPage'),
    });
  };

  const confirmDeleteTemplate = async () => {
    if (!templateToDelete) return;
    const deletedId = templateToDelete.id;
    setDeletingTemplate(true);
    try {
      await segmentTemplatesService.remove(deletedId);
      toast.success('Template deleted');
      setTemplates((prev) => prev.filter((t) => t.id !== deletedId));
      setTemplateToDelete(null);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || 'Failed to delete template');
    } finally {
      setDeletingTemplate(false);
    }
  };

  const applyNameSearch = () => {
    const next = nameSearchInput.trim();
    resetPage();
    setNameContains(next);
  };

  const applyTemplateNameSearch = () => {
    const next = templateNameSearchInput.trim();
    resetTemplatePage();
    setTemplateNameContains(next);
  };

  const handleSortHeaderClick = (field) => {
    resetPage();
    if (sortByField === field) {
      setSortOrder((prev) => (prev === 'asc' ? 'desc' : 'asc'));
      return;
    }
    setSortByField(field);
    setSortOrder(field === 'name' || field === 'id' ? 'asc' : 'desc');
  };

  const renderSortableHeader = (label, field, className) => {
    const isActive = sortByField === field;
    const SortIcon = sortOrder === 'asc' ? ArrowUp : ArrowDown;
    return (
      <th className={className}>
        <button
          type="button"
          onClick={() => handleSortHeaderClick(field)}
          className="inline-flex items-center gap-1 font-medium text-gray-700 hover:text-gray-900"
          aria-label={`Sort by ${label}${isActive ? `, currently ${sortOrder}` : ''}`}
        >
          {label}
          {isActive ? <SortIcon size={14} aria-hidden="true" /> : null}
        </button>
      </th>
    );
  };

  /** Open the editor pre-filled with this segment's criteria as a new, unsaved segment. */
  const handleCopySegment = (segment) => {
    navigate(`/segments/new?from=${segment.id}`, { state: listPageState(currentPage) });
  };

  const handleSegmentFileDownload = (segment) => {
    const orgName = organizations.find((o) => String(o.id) === String(selectedOrganization))?.name;
    if (!orgName || !segment.id) {
      toast.error('Cannot download: missing organization or segment ID');
      return;
    }
    const href = buildFileDownloadHref(`/api/v2/segments/${segment.id}/requested_download`, {
      organization: orgName,
    });
    window.open(href, '_blank', 'noopener,noreferrer');
  };

  const handleRunSegmentDownload = async (segment) => {
    const sql = (segment.sql || '').trim();
    if (!sql) {
      toast.error('Cannot run download: segment has no SQL');
      return;
    }

    setRunningSegmentId(segment.id);
    try {
      const res = await api.post(`/api/v2/segments/${segment.id}/segment_download_request`);
      const executionId = res.data?.execution_id;
      if (executionId) {
        setSegments((prev) =>
          prev.map((s) =>
            s.id === segment.id
              ? { ...s, execution_id: executionId, processing_status: 'pending' }
              : s
          )
        );
      }
      toast.success(
        executionId
          ? `Segment download started (execution ID: ${executionId})`
          : 'Segment download started'
      );
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || 'Failed to start segment download');
    } finally {
      setRunningSegmentId(null);
    }
  };

  const handleDeleteClick = (segment) => {
    setSegmentToDelete(segment);
    setShowDeleteConfirm(true);
  };

  const confirmDelete = async () => {
    if (!segmentToDelete) return;
    const deletedId = segmentToDelete.id;

    setDeleting(true);
    try {
      await api.delete(`/api/v2/segments/${deletedId}`);
      toast.success('Segment deleted');
      setSegments((prev) => prev.filter((s) => s.id !== deletedId));
      if (expandedSegmentId === deletedId) {
        setExpandedSegmentId(null);
      }
      setSegmentToDelete(null);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || 'Failed to delete segment');
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div className="space-y-6">
      <PageHeaderActions>
        <FunctionalSelect
          label="Organization"
          className={HEADER_SELECT_CLASS}
          value={organizationsService.getSelectedOptionValue(
            sortedOrganizations,
            selectedOrganization,
            'id'
          )}
          onValueChange={(value) => {
            const parsed = organizationsService.parseOrganizationOptionValue(value);
            resetPage();
            resetTemplatePage();
            setNameSearchInput('');
            setNameContains('');
            setTemplateNameSearchInput('');
            setTemplateNameContains('');
            if (parsed) {
              setSelectedOrganization(parsed.id);
              organizationsService.setSelectedOrganization(parsed.id, 'id', sortedOrganizations);
            } else {
              setSelectedOrganization(value);
              organizationsService.setSelectedOrganization(value, 'id', organizations);
            }
          }}
          placeholder="Select organization"
        >
          {sortedOrganizations.map((org) => (
            <SelectItem key={org.id} value={organizationsService.orgToOptionValue(org)}>
              {org.name}
            </SelectItem>
          ))}
        </FunctionalSelect>
      </PageHeaderActions>
      <div className="flex space-x-2 border-b">
        <button
          type="button"
          className={`px-4 py-2 -mb-px border-b-2 ${activeTab === 'segments' ? 'border-primary-600 text-primary-700' : 'border-transparent text-gray-600'}`}
          onClick={() => setActiveTab('segments')}
        >
          Segments
        </button>
        <button
          type="button"
          className={`px-4 py-2 -mb-px border-b-2 ${activeTab === 'templates' ? 'border-primary-600 text-primary-700' : 'border-transparent text-gray-600'}`}
          onClick={() => setActiveTab('templates')}
        >
          Templates
        </button>
      </div>

      <Card className="overflow-visible">
        <CardContent className="pt-6 space-y-4 overflow-visible">
          <div className="flex flex-wrap items-end gap-3">
            <div className="ml-auto flex shrink-0 items-center gap-2">
              {activeTab === 'segments' ? (
                <>
                  <button
                    type="button"
                    onClick={() => setTemplatePickerOpen(true)}
                    disabled={!selectedOrganization}
                    className="inline-flex h-10 items-center justify-center gap-1.5 rounded-md border border-hairline bg-surface px-4 py-2 font-medium text-[#18181B] hover:bg-[#E8EEF5] disabled:pointer-events-none disabled:opacity-50"
                    title="Start from a template"
                  >
                    <LayoutTemplate className="h-5 w-5 text-zinc-500" />
                    From template
                  </button>
                  <Button
                    variant="outline"
                    onClick={() => navigate('/segments/new', { state: listPageState(currentPage) })}
                    disabled={!selectedOrganization}
                    aria-label="New segment"
                    className="gap-1.5"
                  >
                    <Plus className="h-5 w-5" />
                    Segment
                  </Button>
                </>
              ) : (
                <Button
                  variant="outline"
                  onClick={() => navigate('/segments/templates/new', { state: listPageState(templateCurrentPage, 'templateListPage') })}
                  disabled={!selectedOrganization}
                  aria-label="New template"
                  className="gap-1.5"
                >
                  <Plus className="h-5 w-5" />
                  Template
                </Button>
              )}
            </div>
          </div>

          {activeTab === 'segments' && selectedOrganization && (
            <div className="flex items-center gap-2 w-full">
              <Input
                type="text"
                placeholder="Search by name..."
                value={nameSearchInput}
                onChange={(e) => setNameSearchInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    applyNameSearch();
                  }
                }}
                aria-label="Search segments by name"
                className="flex-1 min-w-0"
              />
              <Button
                type="button"
                variant="outline"
                size="icon"
                onClick={applyNameSearch}
                className="shrink-0"
                aria-label="Search"
              >
                <Search className="h-5 w-5 text-white" />
              </Button>
            </div>
          )}

          {activeTab === 'templates' && selectedOrganization && (
            <div className="flex items-center gap-2 w-full">
              <Input
                type="text"
                placeholder="Search by name..."
                value={templateNameSearchInput}
                onChange={(e) => setTemplateNameSearchInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    applyTemplateNameSearch();
                  }
                }}
                aria-label="Search templates by name"
                className="flex-1 min-w-0"
              />
              <Button
                type="button"
                variant="outline"
                size="icon"
                onClick={applyTemplateNameSearch}
                className="shrink-0"
                aria-label="Search"
              >
                <Search className="h-5 w-5 text-white" />
              </Button>
            </div>
          )}

          {activeTab === 'segments' && (loading ? (
            <p className="text-sm text-gray-500">Loading segments...</p>
          ) : segments.length === 0 ? (
            <p className="text-sm text-gray-500">
              {nameContains
                ? 'No segments match this search.'
                : 'No segments for this organization.'}
            </p>
          ) : (
            <>
              <div className="border border-gray-200 rounded-md overflow-x-auto">
                <table className="min-w-[64rem] w-full text-sm">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-1 py-2 text-left font-medium text-gray-700 border-b w-9" aria-label="Expand" />
                      {renderSortableHeader('Name', 'name', 'pl-1 pr-3 py-2 text-left border-b')}
                      {renderSortableHeader('ID', 'id', 'px-3 py-2 text-left border-b whitespace-nowrap')}
                      <th className="px-3 py-2 text-left font-medium text-gray-700 border-b whitespace-nowrap">Schedule</th>
                      <th className="px-3 py-2 text-left font-medium text-gray-700 border-b">User</th>
                      <th className="px-3 py-2 text-left font-medium text-gray-700 border-b whitespace-nowrap">MD5 Count</th>
                      <th className="px-3 py-2 text-left font-medium text-gray-700 border-b whitespace-nowrap">Last Run</th>
                      {renderSortableHeader('Created', 'created_at', 'px-3 py-2 text-left border-b whitespace-nowrap')}
                      {renderSortableHeader('Updated', 'updated_at', 'px-3 py-2 text-left border-b whitespace-nowrap')}
                      <th className="px-3 py-2 text-left font-medium text-gray-700 border-b whitespace-nowrap">Status</th>
                      <th
                        className="px-3 py-2 text-left font-medium text-gray-700 border-b whitespace-nowrap w-[8.5rem]"
                        aria-label="Actions"
                      />
                    </tr>
                  </thead>
                  <tbody>
                    {pagedSegments.map((segment) => {
                      const isExpanded = expandedSegmentId === segment.id;
                      return (
                        <React.Fragment key={segment.id}>
                          <tr
                            className="border-b border-gray-100 hover:bg-gray-50 cursor-pointer"
                            onClick={() =>
                              setExpandedSegmentId((prev) => (prev === segment.id ? null : segment.id))
                            }
                          >
                            <td className="px-1 py-2 text-gray-500 w-9" onClick={(e) => e.stopPropagation()}>
                              <button
                                type="button"
                                onClick={() =>
                                  setExpandedSegmentId((prev) => (prev === segment.id ? null : segment.id))
                                }
                                className="p-0.5 rounded hover:bg-gray-200"
                                aria-expanded={isExpanded}
                                aria-label={isExpanded ? 'Collapse SQL' : 'Expand SQL'}
                              >
                                {isExpanded ? <ChevronDown size={18} /> : <ChevronRight size={18} />}
                              </button>
                            </td>
                            <td
                              className="pl-1 pr-3 py-2 max-w-[18rem] whitespace-normal break-words"
                              title={segment.name || undefined}
                              onClick={(e) => e.stopPropagation()}
                            >
                              <button
                                type="button"
                                onClick={() =>
                                  navigate(`/segments/${segment.id}`, { state: listPageState(currentPage) })
                                }
                                className="text-left font-medium text-[#18181B] hover:text-[#0062FB] hover:underline"
                              >
                                {formatSegmentNameForDisplay(segment.name)}
                              </button>
                              {segment.criteria ? (
                                <p className="mt-0.5 line-clamp-2 text-xs text-zinc-500" title={criteriaToText(segment.criteria)}>
                                  {criteriaToText(segment.criteria)}
                                </p>
                              ) : null}
                            </td>
                            <td className="px-3 py-2 text-gray-800 whitespace-nowrap font-mono text-xs">
                              {segment.id}
                            </td>
                            <td className="px-3 py-2 text-gray-800 whitespace-nowrap">
                              {formatSegmentSchedule(segment)}
                            </td>
                            <td className="px-3 py-2 text-gray-800">{segment.user_name || '—'}</td>
                            <td className="px-3 py-2 text-gray-800 whitespace-nowrap">
                              {formatMd5Count(segment.md5_count)}
                            </td>
                            <td className="px-3 py-2 text-gray-800 whitespace-nowrap">
                              {formatDate(segment.last_run)}
                            </td>
                            <td className="px-3 py-2 text-gray-800 whitespace-nowrap">
                              {formatDate(segment.created_at)}
                            </td>
                            <td className="px-3 py-2 text-gray-800 whitespace-nowrap">
                              {formatDate(segment.updated_at)}
                            </td>
                            <td className="px-3 py-2">
                              {segment.processing_status ? (
                                <span
                                  className={`px-2 py-0.5 rounded text-xs font-medium ${segmentDownloadStatusBadgeClass(segment.processing_status)}`}
                                >
                                  {segment.processing_status}
                                </span>
                              ) : (
                                <span className="text-gray-400">—</span>
                              )}
                            </td>
                            <td
                              className="px-3 py-2 text-right whitespace-nowrap"
                              onClick={(e) => e.stopPropagation()}
                              onMouseDown={(e) => e.stopPropagation()}
                            >
                              <div className="inline-flex items-center justify-end gap-1">
                                <button
                                  type="button"
                                  onClick={() =>
                                    navigate(`/segments/${segment.id}`, { state: listPageState(currentPage) })
                                  }
                                  className="p-1 rounded hover:bg-blue-50 text-[#0062FB] shrink-0"
                                  aria-label="Edit"
                                  title="Edit"
                                >
                                  <Pencil className="h-4 w-4" />
                                </button>
                                <button
                                  type="button"
                                  onClick={() => handleRunSegmentDownload(segment)}
                                  disabled={runningSegmentId === segment.id || !selectedOrganization}
                                  className="p-1 rounded hover:bg-blue-50 text-[#0062FB] disabled:opacity-50 shrink-0"
                                  aria-label="Run"
                                  title="Run"
                                >
                                  <Play className="h-4 w-4" />
                                </button>
                                <button
                                  type="button"
                                  onClick={() => handleCopySegment(segment)}
                                  disabled={!selectedOrganization}
                                  className="p-1 rounded hover:bg-blue-50 text-[#0062FB] disabled:opacity-50 shrink-0"
                                  aria-label="Duplicate"
                                  title="Duplicate as a new segment"
                                >
                                  <Copy className="h-4 w-4" />
                                </button>
                                <button
                                  type="button"
                                  onClick={() => handleDeleteClick(segment)}
                                  disabled={deleting}
                                  className="p-1 rounded hover:bg-pink-50 text-[#F53D7A] disabled:opacity-50 shrink-0"
                                  aria-label="Delete"
                                  title="Delete"
                                >
                                  <Trash2 className="h-4 w-4" />
                                </button>
                                {canDownloadSegment(segment) ? (
                                  <button
                                    type="button"
                                    onClick={() => handleSegmentFileDownload(segment)}
                                    className="p-1 rounded hover:bg-blue-50 text-[#0062FB] shrink-0"
                                    aria-label="Download"
                                    title="Download"
                                  >
                                    <Download className="h-4 w-4" />
                                  </button>
                                ) : (
                                  <span className="p-1 w-6 h-6 shrink-0" aria-hidden="true" />
                                )}
                              </div>
                            </td>
                          </tr>
                          {isExpanded && (
                            <tr className="border-b border-gray-100 bg-gray-50">
                              <td colSpan={11} className="px-3 py-3">
                                <div className="text-xs text-gray-600 space-y-3">
                                  {segment.execution_id ? (
                                    <div>
                                      <span className="font-medium text-gray-700">Execution ID:</span>{' '}
                                      <code
                                        className="break-all font-mono bg-gray-100 px-1.5 py-0.5 rounded text-[11px]"
                                        title={segment.execution_id}
                                      >
                                        {segment.execution_id}
                                      </code>
                                    </div>
                                  ) : null}
                                  <div>
                                    <span className="font-medium text-gray-700">SQL:</span>
                                    <pre className="mt-1 text-sm text-gray-800 whitespace-pre-wrap font-mono bg-gray-100 px-3 py-2 rounded overflow-x-auto">
                                      {segment.sql}
                                    </pre>
                                  </div>
                                </div>
                              </td>
                            </tr>
                          )}
                        </React.Fragment>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              <div className="flex items-center justify-between gap-3 pt-3 text-sm text-gray-600">
                <span>
                  {segments.length.toLocaleString()} segment
                  {segments.length === 1 ? '' : 's'}
                  {` · page ${currentPage} of ${totalPages}`}
                </span>
                <ListPagination
                  currentPage={currentPage}
                  totalPages={totalPages}
                  disabled={loading}
                  onPageChange={goToPage}
                />
              </div>
            </>
          ))}

          {activeTab === 'templates' &&
            (templatesLoading ? (
              <p className="text-sm text-gray-500">Loading templates...</p>
            ) : !selectedOrganization ? (
              <p className="text-sm text-gray-500">Select an organization to see its templates.</p>
            ) : filteredTemplates.length === 0 ? (
              <p className="text-sm text-gray-500">
                {templateNameContains
                  ? 'No templates match this search.'
                  : 'No templates for this organization.'}
              </p>
            ) : (
              <>
                <div className="border border-gray-200 rounded-md overflow-x-auto">
                  <table className="min-w-[56rem] w-full text-sm">
                    <thead className="bg-gray-50">
                      <tr>
                        {isFastCubeAdmin ? (
                          <th className="px-1 py-2 text-left font-medium text-gray-700 border-b w-9" aria-label="Expand" />
                        ) : null}
                        {renderTemplateSortableHeader('Name', 'name', `${isFastCubeAdmin ? 'pl-1' : 'pl-3'} pr-3 py-2 text-left border-b`)}
                        {renderTemplateSortableHeader('User', 'user_name', 'px-3 py-2 text-left border-b whitespace-nowrap')}
                        {renderTemplateSortableHeader('MD5 Count', 'md5_count', 'px-3 py-2 text-left border-b whitespace-nowrap')}
                        {renderTemplateSortableHeader('Created', 'created_at', 'px-3 py-2 text-left border-b whitespace-nowrap')}
                        {renderTemplateSortableHeader('Updated', 'updated_at', 'px-3 py-2 text-left border-b whitespace-nowrap')}
                        <th
                          className="px-3 py-2 text-left font-medium text-gray-700 border-b whitespace-nowrap w-[6.5rem]"
                          aria-label="Actions"
                        />
                      </tr>
                    </thead>
                    <tbody>
                      {pagedTemplates.map((template) => {
                        const isExpanded = isFastCubeAdmin && expandedTemplateId === template.id;
                        const templateColSpan = isFastCubeAdmin ? 7 : 6;
                        return (
                          <React.Fragment key={template.id}>
                            <tr
                              className={`border-b border-gray-100 hover:bg-gray-50${isFastCubeAdmin ? ' cursor-pointer' : ''}`}
                              onClick={
                                isFastCubeAdmin
                                  ? () =>
                                      setExpandedTemplateId((prev) => (prev === template.id ? null : template.id))
                                  : undefined
                              }
                            >
                              {isFastCubeAdmin ? (
                                <td className="px-1 py-2 text-gray-500 w-9" onClick={(e) => e.stopPropagation()}>
                                  <button
                                    type="button"
                                    onClick={() =>
                                      setExpandedTemplateId((prev) => (prev === template.id ? null : template.id))
                                    }
                                    className="p-0.5 rounded hover:bg-gray-200"
                                    aria-expanded={isExpanded}
                                    aria-label={isExpanded ? 'Collapse SQL' : 'Expand SQL'}
                                  >
                                    {isExpanded ? <ChevronDown size={18} /> : <ChevronRight size={18} />}
                                  </button>
                                </td>
                              ) : null}
                              <td
                                className={`${isFastCubeAdmin ? 'pl-1' : 'pl-3'} pr-3 py-2 max-w-[22rem] whitespace-normal break-words`}
                                title={template.name || undefined}
                                onClick={(e) => e.stopPropagation()}
                              >
                                <button
                                  type="button"
                                  onClick={() =>
                                    navigate(`/segments/templates/${template.id}`, {
                                      state: listPageState(templateCurrentPage, 'templateListPage'),
                                    })
                                  }
                                  className="text-left font-medium text-[#18181B] hover:text-[#0062FB] hover:underline"
                                >
                                  {formatSegmentNameForDisplay(template.name)}
                                </button>
                                {template.description ? (
                                  <p className="mt-0.5 line-clamp-2 text-xs text-zinc-500" title={template.description}>
                                    {template.description}
                                  </p>
                                ) : null}
                              </td>
                              <td className="px-3 py-2 text-gray-800">{template.user_name || '—'}</td>
                              <td className="px-3 py-2 text-gray-800 whitespace-nowrap">
                                {formatMd5Count(template.md5_count)}
                              </td>
                              <td className="px-3 py-2 text-gray-800 whitespace-nowrap">{formatDate(template.created_at)}</td>
                              <td className="px-3 py-2 text-gray-800 whitespace-nowrap">{formatDate(template.updated_at)}</td>
                              <td
                                className="px-3 py-2 text-right whitespace-nowrap"
                                onClick={(e) => e.stopPropagation()}
                              >
                                <div className="inline-flex items-center justify-end gap-1">
                                  <button
                                    type="button"
                                    onClick={() =>
                                      navigate(`/segments/templates/${template.id}`, {
                                        state: listPageState(templateCurrentPage, 'templateListPage'),
                                      })
                                    }
                                    className="p-1 rounded hover:bg-blue-50 text-[#0062FB] shrink-0"
                                    aria-label="Edit"
                                    title="Edit"
                                  >
                                    <Pencil className="h-4 w-4" />
                                  </button>
                                  <button
                                    type="button"
                                    onClick={() => handleCopyTemplate(template)}
                                    disabled={!selectedOrganization}
                                    className="p-1 rounded hover:bg-blue-50 text-[#0062FB] disabled:opacity-50 shrink-0"
                                    aria-label="Duplicate"
                                    title="Duplicate as a new template"
                                  >
                                    <Copy className="h-4 w-4" />
                                  </button>
                                  {canManageTemplate(template) ? (
                                    <button
                                      type="button"
                                      onClick={() => handleDeleteTemplateClick(template)}
                                      disabled={deletingTemplate}
                                      className="p-1 rounded hover:bg-pink-50 text-[#F53D7A] disabled:opacity-50 shrink-0"
                                      aria-label="Delete"
                                      title="Delete"
                                    >
                                      <Trash2 className="h-4 w-4" />
                                    </button>
                                  ) : null}
                                </div>
                              </td>
                            </tr>
                            {isExpanded ? (
                              <tr className="border-b border-gray-100 bg-gray-50">
                                <td colSpan={templateColSpan} className="px-3 py-3">
                                  <div className="text-xs text-gray-600 space-y-3">
                                    <div>
                                      <span className="font-medium text-gray-700">SQL:</span>
                                      <pre className="mt-1 text-sm text-gray-800 whitespace-pre-wrap font-mono bg-gray-100 px-3 py-2 rounded overflow-x-auto">
                                        {template.sql || '—'}
                                      </pre>
                                    </div>
                                  </div>
                                </td>
                              </tr>
                            ) : null}
                          </React.Fragment>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
                <div className="flex items-center justify-between gap-3 pt-3 text-sm text-gray-600">
                  <span>
                    {filteredTemplates.length.toLocaleString()} template
                    {filteredTemplates.length === 1 ? '' : 's'}
                    {` · page ${templateCurrentPage} of ${templateTotalPages}`}
                  </span>
                  <ListPagination
                    currentPage={templateCurrentPage}
                    totalPages={templateTotalPages}
                    disabled={templatesLoading}
                    onPageChange={goToTemplatePage}
                  />
                </div>
              </>
            ))}
        </CardContent>
      </Card>

      <TemplatePicker
        isOpen={templatePickerOpen}
        onClose={() => setTemplatePickerOpen(false)}
        onPick={(t) => {
          setTemplatePickerOpen(false);
          navigate(`/segments/new?template=${t.id}`, { state: listPageState(currentPage) });
        }}
        organizationId={selectedOrganization}
        isFastCubeAdmin={isFastCubeAdmin}
      />

      <ErrorDialog
        isOpen={showErrorDialog}
        onClose={() => setShowErrorDialog(false)}
        message={errorMessage}
      />

      <ConfirmDialog
        isOpen={showDeleteConfirm}
        onClose={() => {
          setShowDeleteConfirm(false);
          setSegmentToDelete(null);
        }}
        onConfirm={confirmDelete}
        title="Delete Segment"
        message={
          segmentToDelete
            ? `Are you sure you want to delete segment "${segmentToDelete.name}"?`
            : ''
        }
        confirmText="OK"
      />

      <ConfirmDialog
        isOpen={showDeleteTemplateConfirm}
        onClose={() => {
          setShowDeleteTemplateConfirm(false);
          setTemplateToDelete(null);
        }}
        onConfirm={confirmDeleteTemplate}
        title="Delete Template"
        message={
          templateToDelete
            ? `Are you sure you want to delete template "${templateToDelete.name}"?`
            : ''
        }
        confirmText="OK"
      />
    </div>
  );
};

export default Segments;
